import { useEffect, useState, type FormEvent } from "react";
import { useSearchParams } from "react-router-dom";
import { CalendarCheck, ChevronRight, PhoneCall } from "lucide-react";
import { formatDate } from "../api";
import { useClientSelection } from "../client-context";
import { useApi } from "../hooks";
import { PageHeader, MetricCard } from "../components/Ui";
import Markdown from "../components/Markdown";
import {
  Details,
  NoClient,
  RequestView,
  sentence,
  useMutation,
  type RecordData,
} from "../components/Workflow";

function CallCenterUploadForm({ onSubmit, busy }: { onSubmit: (event: FormEvent<HTMLFormElement>) => void; busy: boolean }) {
  const [calls, setCalls] = useState(''), [appointments, setAppointments] = useState('');
  return <form className="callcenter-form" onSubmit={onSubmit}>
    <div className="callcenter-drop-grid">
      <label className="callcenter-drop"><em>Required</em><span className="callcenter-drop-icon" aria-hidden="true"><PhoneCall /></span><strong>Call center export</strong><span>JunkWare, CallTrackingMetrics or GHL export (.xlsx, .xls or .csv)</span><span className="button secondary">Choose files</span><small>{calls || 'No files selected'}</small><input name="call_center_csv" type="file" accept=".csv,.xlsx,.xls" multiple required onChange={event => setCalls([...event.target.files || []].map(file => file.name).join(', '))} /></label>
      <label className="callcenter-drop"><em>Optional</em><span className="callcenter-drop-icon" aria-hidden="true"><CalendarCheck /></span><strong>Appointment export</strong><span>Appointments or jobs from the booking system (.xlsx, .xls or .csv)</span><span className="button secondary">Choose files</span><small>{appointments || 'No files selected'}</small><input name="appointment_csv" type="file" accept=".csv,.xlsx,.xls" multiple onChange={event => setAppointments([...event.target.files || []].map(file => file.name).join(', '))} /></label>
    </div>
    <p className="callcenter-info">Add the appointment export to include revenue, completion rates and ROAS.</p>
    <div className="callcenter-period"><label>Period start <input name="period_start" type="date" required /></label><label>Period end <input name="period_end" type="date" required /></label></div>
    <details className="callcenter-options"><summary>Report options</summary><div className="callcenter-options-grid">
      <label>Location filter <input name="location_token" placeholder="sacramento" /><small>Use a territory name to exclude other franchise locations. Leave blank to include every row.</small></label>
      <label>Average job size <input name="avg_job_size" type="number" min="0" step=".01" placeholder="$" /><small>Used only when no appointment export is uploaded. Leave blank for counts only.</small></label>
      <label>AJS basis <select name="ajs_basis"><option value="mean">Mean</option><option value="median">Median</option></select><small>Mean reflects total revenue; median reflects a typical job.</small></label>
      <label className="checkbox-line"><input name="exclude_national_accounts" type="checkbox" value="true" />Exclude National Accounts</label>
      <p className="muted">National Accounts are corporate-referred work, not marketing-driven demand. Turning this on reports marketing performance only.</p>
    </div></details>
    <div className="callcenter-submit"><p>Files up to 4 MB in total.</p><button className="button" disabled={busy} type="submit">{busy ? 'Generating…' : 'Generate report'}</button></div>
  </form>;
}

/** What a report was built from, in words: "With appointments", "Calls only", plus "Client upload" when the client sent it. */
const reportKind = (row: RecordData) =>
  [row.has_appointment_data ? 'With appointments' : 'Calls only', row.upload_mode === 'client_portal' ? 'Client upload' : null].filter(Boolean).join(', ');
/** Where the written analysis is, in words, for a report still being written or one that failed. */
function narrativeState(status: unknown, error: unknown) {
  if (['generating', 'pending', 'queued', 'processing'].includes(String(status))) return 'The written analysis is still being prepared. This page checks again every 10 seconds.';
  if (status === 'failed') return `The written analysis could not be prepared.${error ? ` ${String(error)}` : ''}`;
  return `Written analysis: ${String(status ?? 'not started').replace(/_/g, ' ')}.${error ? ` ${String(error)}` : ''}`;
}

const rate = (value: unknown) => value == null ? '—' : `${Number(value).toFixed(1)}%`;
const warningMessage = (warning: unknown) => {
  if (typeof warning === 'string') return warning;
  if (warning && typeof warning === 'object' && 'message' in warning && typeof warning.message === 'string') return warning.message;
  return JSON.stringify(warning);
};
function ReportsWorkspace({ clientId }: { clientId: number }) {
  const [params, setParams] = useSearchParams();
  const id = params.get("report");
  const reports = useApi<RecordData[]>(`/api/callcenter/${clientId}/reports`),
    report = useApi<RecordData>(
      id ? `/api/callcenter/${clientId}/reports/${id}` : null,
    );
  const [view, setView] = useState<'new' | 'history'>(id ? 'history' : 'new');
  const [question, setQuestion] = useState(""),
    [answer, setAnswer] = useState("");
  const mutation = useMutation();
  const select = (value?: string) => {
    const next = new URLSearchParams(params);
    value ? next.set("report", value) : next.delete("report");
    setParams(next);
    setAnswer("");
  };
  useEffect(() => {
    if (
      !id ||
      !["generating", "pending", "queued", "processing"].includes(
        report.data?.narrative_status,
      )
    )
      return;
    const timer = setInterval(() => report.reload(), 10000);
    return () => clearInterval(timer);
  }, [id, report.data?.narrative_status, report.reload]);
  async function uploadFiles(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const body = new FormData(e.currentTarget);
    body.set("client_id", String(clientId));
    if (String(body.get("period_end")) < String(body.get("period_start"))) {
      mutation.setNotice("Period end must be on or after its start.");
      return;
    }
    const files = [...body.values()].filter((v) => v instanceof File) as File[];
    if (files.reduce((n, f) => n + f.size, 0) > 4 * 1024 * 1024) {
      mutation.setNotice(
        "These files add up to more than 4 MB. Split a larger export by period and upload each part.",
      );
      return;
    }
    const result = await mutation.run<RecordData>(
      "/api/callcenter/upload",
      body,
    );
    if (result) {
      setView('history');
      reports.reload();
      select(String(result.report_id));
      mutation.setNotice(
        "Report uploaded. The written analysis takes a minute or two.",
      );
    }
  }
  return (
    <div className="workflow-stack callcenter-workspace">
      {/* The tabs are the only way between the two views: a second "New report" button beside them said the same thing. */}
      <div className="callcenter-viewbar"><div role="tablist" aria-label="Call center views" className="callcenter-tabs"><button role="tab" aria-selected={view === 'new'} className={view === 'new' ? 'active' : ''} onClick={() => setView('new')}>New report</button><button role="tab" aria-selected={view === 'history'} className={view === 'history' ? 'active' : ''} onClick={() => setView('history')}>History</button></div></div>
      {view === 'new' ? <section className="panel callcenter-new" role="tabpanel" aria-label="New report"><CallCenterUploadForm onSubmit={uploadFiles} busy={mutation.busy} /></section> : <section className="callcenter-history" role="tabpanel" aria-label="History"><div className="callcenter-history-head"><h2>Report history</h2><button className="button secondary" onClick={reports.reload}>Refresh</button></div><RequestView request={reports}>{!reports.data?.length ? <p className="panel callcenter-empty">No reports for this client yet. Use New report to upload exports.</p> : <div className="q-grid-wrap panel"><table className="q-grid callcenter-history-table"><thead><tr><th>Period</th><th>Report</th><th className="num">Calls</th><th className="num">Answer %</th><th className="num" title="Appointments booked on these calls, from the call center export">Booked from calls</th><th className="num">Booking %</th><th className="num" title="From the appointment export, all channels">Completed jobs</th><th className="num">ROAS</th><th>Uploaded</th><th className="q-grid-go"><span className="sr-only">Open</span></th></tr></thead><tbody>{reports.data.map(row => <tr key={String(row.id)} tabIndex={0} onClick={() => select(String(row.id))} onKeyDown={e => { if (e.key === 'Enter') select(String(row.id)); }} aria-label={`Open report ${row.period_label || ''}`}><td className="q-grid-title" data-label="Period">{row.period_label || `${formatDate(row.period_start)} to ${formatDate(row.period_end)}`}</td><td className="q-grid-muted" data-label="Report">{reportKind(row)}</td><td className="num" data-label="Calls">{Number(row.total_calls || 0).toLocaleString()}</td><td className="num" data-label="Answer %">{rate(row.answer_rate)}</td><td className="num" data-label="Booked from calls">{row.scheduled_appointments == null ? '—' : Number(row.scheduled_appointments).toLocaleString()}</td><td className="num" data-label="Booking %">{rate(row.booking_rate)}</td><td className="num" data-label="Completed jobs" title="From the appointment export, all channels">{row.completed_jobs == null ? '—' : Number(row.completed_jobs).toLocaleString()}</td><td className="num" data-label="ROAS">{row.implied_roas == null ? '—' : `${Number(row.implied_roas).toFixed(1)}×`}</td><td className="q-grid-muted" data-label="Uploaded">{formatDate(row.created_at)}</td><td className="q-grid-go"><ChevronRight aria-hidden="true" /></td></tr>)}</tbody></table></div>}</RequestView></section>}
      {mutation.feedback}
      {view === 'history' && id && (
        <RequestView request={report}>
          {report.data && (
            <section className="panel workflow-panel">
              <div className="panel-heading">
                <h2>{report.data.period?.label || "Call center report"}</h2>
                <button className="button secondary" onClick={() => select()}>
                  Close report
                </button>
              </div>
              <div className="module-metrics">
                <MetricCard
                  label="Calls"
                  value={report.data.key_metrics?.total_calls ?? "—"}
                  detail="Recorded calls"
                />
                <MetricCard
                  label="Answered"
                  value={report.data.key_metrics?.answered_calls ?? "—"}
                  detail="Confirmed answer status"
                />
                <MetricCard
                  label="Booked"
                  value={report.data.key_metrics?.scheduled_appointments ?? "—"}
                  detail="Recorded appointments"
                />
                <MetricCard
                  label="ROAS"
                  value={
                    report.data.revenue?.implied_roas == null
                      ? "—"
                      : `${report.data.revenue.implied_roas}×`
                  }
                  detail="Matched spend and revenue"
                />
              </div>
              {report.data.warnings?.length > 0 && (
                <div className={`inline-notice ${report.data.warnings.some((w: RecordData) => w?.code === 'client_location_mismatch') ? 'danger' : ''}`}>
                  {[...report.data.warnings].sort((a: RecordData, b: RecordData) => Number(b?.code === 'client_location_mismatch') - Number(a?.code === 'client_location_mismatch')).map((w: unknown, i: number) => (
                    <p key={i}>
                      {warningMessage(w)}
                    </p>
                  ))}
                </div>
              )}
              {report.data.narrative_status !== "complete" && (
                <p className="workflow-note">
                  {narrativeState(report.data.narrative_status, report.data.narrative_error)}
                </p>
              )}
              {report.data.client_summary && (
                <>
                  <h2>Client summary</h2>
                  <Markdown>{report.data.client_summary}</Markdown>
                </>
              )}
              {report.data.full_report && (
                <details className="report-section">
                  <summary>Full analysis</summary>
                  <Markdown>{report.data.full_report}</Markdown>
                </details>
              )}
              {[
                "key_metrics",
                "revenue",
                "appointments",
                "cross_reference",
                "breakdowns",
                "location_breakdown",
                "period_over_period",
                "roas_by_platform",
                "roas_by_territory",
                "callback_lists",
                "missed_call_recovery",
              ]
                .filter((k) => report.data?.[k] != null)
                .map((k) => (
                  <details className="report-section" key={k}>
                    <summary>{sentence(k)}</summary>
                    <Details
                      record={
                        typeof report.data![k] === "object" &&
                        !Array.isArray(report.data![k])
                          ? report.data![k]
                          : { records: report.data![k] }
                      }
                    />
                  </details>
                ))}
              <form
                className="operation-form"
                onSubmit={async (e) => {
                  e.preventDefault();
                  const result = await mutation.run<RecordData>(
                    `/api/callcenter/${clientId}/reports/${id}/ask`,
                    { question },
                  );
                  if (result)
                    setAnswer(String(result.answer || result.response || ""));
                }}
              >
                <h2>Ask this report</h2>
                <label>
                  Question
                  <textarea
                    required
                    maxLength={1000}
                    rows={3}
                    value={question}
                    onChange={(e) => setQuestion(e.target.value)}
                  />
                </label>
                <p className="muted">
                  Answers are written by AI from this report's numbers.
                </p>
                <button
                  className="button"
                  disabled={mutation.busy || !question.trim()}
                >
                  {mutation.busy ? "Asking…" : "Ask analyst"}
                </button>
                {answer && <Markdown>{answer}</Markdown>}
              </form>
              <details className="workflow-panel">
                <summary>Link verified spend for this period</summary>
                <form
                  className="operation-form"
                  onSubmit={async (e) => {
                    e.preventDefault();
                    const fields = new FormData(e.currentTarget);
                    const result = await mutation.run(
                      `/api/callcenter/${id}/link-ad-spend`,
                      { ad_spend: Number(fields.get("ad_spend")) },
                    );
                    if (result) {
                      report.reload();
                      reports.reload();
                      mutation.setNotice("Period spend linked.");
                    }
                  }}
                >
                  <label>
                    Ad spend ($)
                    <input
                      name="ad_spend"
                      type="number"
                      min=".01"
                      step=".01"
                      required
                    />
                  </label>
                  <p className="muted">
                    Use the total for this report’s exact period. This updates
                    its ROAS calculation.
                  </p>
                  <button className="button secondary" disabled={mutation.busy}>
                    Link spend
                  </button>
                </form>
              </details>
            </section>
          )}
        </RequestView>
      )}
    </div>
  );
}
export default function Reports() {
  const { clientId } = useClientSelection();
  return (
    <>
      <PageHeader
        title="Call center reports"
        description="Upload call center exports and get a written report."
      />
      {clientId ? (
        <ReportsWorkspace key={clientId} clientId={clientId} />
      ) : (
        <NoClient />
      )}
    </>
  );
}
